import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Course, Module, Lesson } from "@/lib/types";
import { fetchCourse } from "@/lib/courses";
import { toast } from "@/components/ui/use-toast";
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({ html: true });

interface UseLessonDataProps {
  courseSlug: string | undefined;
  moduleSlug: string | undefined;
  lessonSlug: string | undefined;
  userId: string | undefined;
}

export function useLessonData({ courseSlug, moduleSlug, lessonSlug, userId }: UseLessonDataProps) {
  const navigate = useNavigate();
  const [courseTitle, setCourseTitle] = useState("");
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [allModules, setAllModules] = useState<Module[]>([]);
  const [allLessons, setAllLessons] = useState<Record<string, Lesson[]>>({});
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [completedLessons, setCompletedLessons] = useState<Record<string, boolean>>({});

  useEffect(() => {
    async function loadLessonAndCourse() {
      setLoading(true);
      
      if (!courseSlug || !lessonSlug || !userId) {
        navigate(userId ? `/courses/${courseSlug || ""}` : "/auth?mode=login");
        return;
      }
      
      // Load course structure - this now loads from index.md rather than structure.json
      const courseData = await fetchCourse(courseSlug);
      
      if (!courseData) {
        console.error("Course not found");
        navigate("/courses");
        return;
      }
      
      // Access properties directly from courseData (Course type)
      setCourseTitle(courseData.title);
      setAllModules(courseData.modules || []); // Use modules array from Course
      setAllLessons(courseData.lessons || {}); // Use lessons map from Course
      
      // Check access using direct property
      if (!courseData.is_free) {
        // Check access for non-free course
        const { data: accessData, error: accessError } = await supabase
          .from("user_course_access")
          .select("*")
          .eq("user_id", userId)
          .eq("course_slug", courseSlug)
          .single();
        
        if ((accessError && accessError.code !== "PGRST116") || !accessData) {
          console.error("User doesn't have access to this course");
          navigate(`/courses/${courseSlug}`);
          return;
        }
      }
      
      // Find the current module using moduleSlug (from URL params, corresponds to module.id)
      const foundModule = (courseData.modules || []).find(m => m.id === moduleSlug);
      
      if (!foundModule) {
        console.error("Module not found:", moduleSlug);
        navigate(`/courses/${courseSlug}`);
        return;
      }
      
      // Find the current lesson in the found module's lessons array
      const foundLesson = foundModule.lessons.find(l => l.slug === lessonSlug);
      
      if (!foundLesson) {
        console.error("Lesson not found:", lessonSlug);
        navigate(`/courses/${courseSlug}`);
        return;
      }
      
      // Set state with correct types
      setCurrentLesson(foundLesson);
      setCurrentModule(foundModule);
      
      if (foundLesson.content) {
        setContent(md.render(foundLesson.content));
      } else {
        setContent("<h1>Lesson content not available</h1>");
        console.warn("Lesson content missing for:", courseSlug, moduleSlug, lessonSlug);
      }
      
      // Fetch completed lessons from user_course_access
      if(courseSlug && userId) { 
        const { data: courseAccessData } = await supabase
          .from("user_course_access")
          .select("completed_sections")
          .eq("user_id", userId)
          .eq("course_slug", courseSlug)
          .single();
        
        if (courseAccessData && courseAccessData.completed_sections) {
          const completedSlugs = courseAccessData.completed_sections as string[];
          const completionMap: Record<string, boolean> = {};
          completedSlugs.forEach(slug => {
            completionMap[slug] = true;
          });
          setCompletedLessons(completionMap);
        }
      }
      
      setLoading(false);
    }
    
    loadLessonAndCourse();
  }, [courseSlug, moduleSlug, lessonSlug, navigate, userId]);

  const toggleLessonComplete = async () => {
    if (!userId || !currentLesson || !courseSlug) {
      console.error("Missing required data:", { userId, currentLesson, courseSlug });
      return;
    }
    
    try {
      console.log("Toggling lesson completion status...");
      
      // Get current course access data
      const { data: courseAccessData, error: fetchError } = await supabase
        .from("user_course_access")
        .select("completed_sections")
        .eq("user_id", userId)
        .eq("course_slug", courseSlug)
        .single();
      
      console.log("Current access data:", courseAccessData, "Fetch error:", fetchError);
      
      if (fetchError && fetchError.code !== "PGRST116") {
        console.error("Error fetching course access:", fetchError);
        throw fetchError;
      }
      
      // Get current completed sections or initialize empty array
      let completedSections: string[] = [];
      if (courseAccessData?.completed_sections) {
        completedSections = Array.isArray(courseAccessData.completed_sections) 
          ? courseAccessData.completed_sections as string[]
          : [];
        
        console.log("Current completed sections:", completedSections);
      }
      
      // Check if current lesson is already completed
      const isCurrentlyCompleted = completedSections.includes(currentLesson.slug);
      console.log("Is lesson currently completed:", isCurrentlyCompleted);
      
      // Toggle lesson completion status
      if (isCurrentlyCompleted) {
        // Remove lesson from completed sections
        completedSections = completedSections.filter(slug => slug !== currentLesson.slug);
      } else {
        // Add lesson to completed sections
        completedSections.push(currentLesson.slug);
      }
      
      console.log("Updated completed sections:", completedSections);
      
      // Calculate the total number of lessons for progress calculation
      // With the new structure, allLessons maps lesson slugs to lessons.
      // The total count is the number of keys in this map.
      const totalSections = Object.keys(allLessons).length;
      
      console.log("[toggleLessonComplete] Total sections:", totalSections);

      let newProgress = 0;
      if (totalSections > 0) {
        newProgress = Math.min(Math.round((completedSections.length / totalSections) * 100), 100);
      }
      
      console.log("New progress:", newProgress);
      
      // Check if a record already exists for this user and course
      const { data: existingRecord } = await supabase
        .from("user_course_access")
        .select("id")
        .eq("user_id", userId)
        .eq("course_slug", courseSlug)
        .single();
      
      console.log("Existing record check:", existingRecord);
      
      let updateResult;
      
      if (existingRecord) {
        // Update the existing record
        updateResult = await supabase
          .from("user_course_access")
          .update({
            completed_sections: completedSections,
            progress: newProgress
          })
          .eq("user_id", userId)
          .eq("course_slug", courseSlug);
        
        console.log("Update result:", updateResult);
      } else {
        // Insert a new record if one doesn't exist
        updateResult = await supabase
          .from("user_course_access")
          .insert({
            user_id: userId,
            course_slug: courseSlug,
            completed_sections: completedSections,
            progress: newProgress
          });
        
        console.log("Insert result:", updateResult);
      }
      
      if (updateResult.error) {
        console.error("Database update error:", updateResult.error);
        throw updateResult.error;
      }
      
      // Update local state
      const newCompletedLessons = { ...completedLessons };
      if (isCurrentlyCompleted) {
        newCompletedLessons[currentLesson.slug] = false;
      } else {
        newCompletedLessons[currentLesson.slug] = true;
      }
      setCompletedLessons(newCompletedLessons);
      
      // Show toast notification
      toast({
        title: isCurrentlyCompleted ? "Lesson marked as incomplete" : "Lesson completed",
        description: isCurrentlyCompleted 
          ? "This lesson has been marked as incomplete." 
          : "Your progress has been updated.",
      });
      
      console.log("Lesson toggle complete - UI updated");
    } catch (err) {
      console.error("Error updating lesson completion status:", err);
      toast({
        title: "Error",
        description: "Failed to update lesson status.",
        variant: "destructive",
      });
    }
  };

  const getAdjacentLessons = () => {
    if (!currentModule || !currentLesson) return { prev: null, next: null };
    
    const allModulesOrdered = [...allModules].sort((a, b) => a.position - b.position);
    const currentModuleIndex = allModulesOrdered.findIndex(m => m.id === currentModule.id);
    
    if (currentModuleIndex === -1) return { prev: null, next: null };
    
    // Get lessons directly from the current module's 'lessons' array
    const currentModuleLessons = currentModule.lessons || [];
    const sortedLessons = [...currentModuleLessons].sort((a, b) => a.position - b.position);
    const currentLessonIndex = sortedLessons.findIndex(l => l.slug === currentLesson.slug);
    
    if (currentLessonIndex === -1) return { prev: null, next: null };
    
    let prev = null;
    let next = null;
    
    // Previous lesson: either previous lesson in current module or last lesson in previous module
    if (currentLessonIndex > 0) {
      prev = { ...sortedLessons[currentLessonIndex - 1], moduleSlug: currentModule.id };
    } else if (currentModuleIndex > 0) {
      const prevModule = allModulesOrdered[currentModuleIndex - 1];
      // Get lessons directly from the previous module's 'lessons' array
      const prevModuleLessons = [...(prevModule.lessons || [])].sort((a, b) => a.position - b.position);
      if (prevModuleLessons.length > 0) {
        prev = { ...prevModuleLessons[prevModuleLessons.length - 1], moduleSlug: prevModule.id };
      }
    }
    
    // Next lesson: either next lesson in current module or first lesson in next module
    if (currentLessonIndex < sortedLessons.length - 1) {
      next = { ...sortedLessons[currentLessonIndex + 1], moduleSlug: currentModule.id };
    } else if (currentModuleIndex < allModulesOrdered.length - 1) {
      const nextModule = allModulesOrdered[currentModuleIndex + 1];
      // Get lessons directly from the next module's 'lessons' array
      const nextModuleLessons = [...(nextModule.lessons || [])].sort((a, b) => a.position - b.position);
      if (nextModuleLessons.length > 0) {
        next = { ...nextModuleLessons[0], moduleSlug: nextModule.id };
      }
    }
    
    return { prev, next };
  };

  return {
    courseTitle,
    currentModule,
    currentLesson,
    allModules,
    allLessons,
    content,
    loading,
    completedLessons,
    markLessonComplete: toggleLessonComplete,
    getAdjacentLessons,
  };
}
